import { useEffect, useEffectEvent, useId, useState } from "react";
import type { EmbedFlowProps } from "@formsort/react-embed";

import { Spinner } from "@/components/spinner";
import { getMockFlowOutcomes, type MockFlowOutcome } from "@/mocks/formsort/flow-outcomes";
import { MOCK_LATENCY_MS } from "@/mocks/latency";
import { Button } from "@/ui/button";
import { Card } from "@/ui/card";
import { Heading } from "@/ui/heading";

type FlowEventPayload = Parameters<NonNullable<EmbedFlowProps["onFlowFinalized"]>>[0];

const MOCK_VARIANT_REVISION_UUID = "00000000-0000-4000-8000-000000000000";

const EmbedFlow = ({
  clientLabel,
  embedConfig,
  flowLabel,
  formsortEnv,
  onFlowFinalized,
  onFlowLoaded,
  onStepCompleted,
  queryParams,
  responderUuid,
  variantLabel,
}: EmbedFlowProps) => {
  const titleId = useId();
  const [isLoading, setIsLoading] = useState(true);
  const [finalizedOutcome, setFinalizedOutcome] = useState<MockFlowOutcome | null>(null);
  const outcomes = getMockFlowOutcomes(flowLabel);

  const createEventPayload = (step?: { stepId: string; stepIndex: number }): FlowEventPayload => {
    const uuid = responderUuid ?? MOCK_VARIANT_REVISION_UUID;

    return {
      responder: { responderUuid: uuid, sessionUuid: uuid },
      variantRevisionUuid: MOCK_VARIANT_REVISION_UUID,
      ...step,
    };
  };

  const notifyFlowLoaded = useEffectEvent(() => {
    setIsLoading(false);
    onFlowLoaded?.(createEventPayload());
  });

  useEffect(() => {
    const timeoutId = setTimeout(notifyFlowLoaded, MOCK_LATENCY_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const finalizeFlow = (outcome: MockFlowOutcome, stepIndex: number) => {
    const payload = createEventPayload({ stepId: outcome.stepId, stepIndex });

    setFinalizedOutcome(outcome);
    onStepCompleted?.(payload);
    onFlowFinalized?.(payload);
  };

  return (
    <div
      className="flex items-center justify-center bg-canvas px-4 py-10"
      style={{ width: embedConfig?.style?.width, height: embedConfig?.style?.height }}
    >
      <Card
        aria-busy={isLoading}
        aria-labelledby={titleId}
        className="flex w-full max-w-xl flex-col gap-6 p-6 sm:p-10"
      >
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-content-muted">Mocked integration</p>
          <Heading id={titleId} level={1}>
            Formsort flow (mocked)
          </Heading>
          <p className="text-content-secondary">
            This build swaps the Formsort embed for a local stand-in. Choose how the flow ends to
            carry on through the real app.
          </p>
        </div>

        <dl className="flex flex-col gap-2 rounded-xl bg-surface-sunken p-4 text-sm">
          <div className="flex gap-2">
            <dt className="font-semibold text-content">Flow</dt>
            <dd className="break-all text-content-secondary">{flowLabel}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-semibold text-content">Variant</dt>
            <dd className="break-all text-content-secondary">{variantLabel ?? "default"}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-semibold text-content">Client</dt>
            <dd className="break-all text-content-secondary">
              {clientLabel} ({formsortEnv ?? "production"})
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-semibold text-content">Prefilled answers</dt>
            <dd className="text-content-secondary">{queryParams?.length ?? 0}</dd>
          </div>
        </dl>

        {isLoading ? (
          <div className="flex items-center gap-4" role="status">
            <Spinner className="size-8" />
            <p className="text-content-secondary">Loading the flow…</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Heading level={2}>How should this flow end?</Heading>

            <ul className="flex flex-col gap-4">
              {outcomes.map((outcome, index) => {
                return (
                  <li className="flex flex-col gap-1" key={outcome.stepId}>
                    <Button
                      className="w-full"
                      disabled={!!finalizedOutcome}
                      onClick={() => {
                        finalizeFlow(outcome, index);
                      }}
                      variant={index === 0 ? "primary" : "secondary-dark"}
                    >
                      {outcome.label}
                    </Button>
                    <p className="px-2 text-sm text-content-muted">{outcome.description}</p>
                  </li>
                );
              })}
            </ul>

            {finalizedOutcome ? (
              <p className="text-sm text-content-secondary" role="status">
                Flow finalized at step {finalizedOutcome.stepId}.
              </p>
            ) : null}
          </div>
        )}
      </Card>
    </div>
  );
};

export default EmbedFlow;
